import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";
import type {
  ConvoBlock,
  FollowupChip,
} from "@/pages/Assistant/assistant.types";

/**
 * Concierge API (REP-211) — the AI assistant at /assistant.
 *
 * The streaming chat endpoint (`POST /api/concierge/chat`) is NOT here: RTK
 * Query can't consume an SSE body, so it's driven by a raw `fetch` in
 * useConciergeStream.ts (which attaches the same Supabase Bearer this base API
 * injects via prepareHeaders).
 *
 * This slice owns the one non-streaming round-trip: `confirmAction` →
 * `POST /api/concierge/confirm`. The user taps an ActionCard's primary button,
 * we POST { actionId, args? } (PINNED contract): the server already holds the
 * vetted proposal keyed by `actionId`; `args` are the user's field edits the
 * server re-validates as overrides. The server applies the change and returns
 * the success copy + follow-ups.
 */

export interface ConfirmActionRequest {
  /** Server-issued id of the vetted proposal (from the `action` SSE frame). */
  actionId: string;
  /** Edited field values, sent as overrides the server re-validates. */
  args?: Record<string, unknown>;
}

export interface ConfirmActionResponse {
  /** Opaque server result payload (e.g. the created record id). */
  result: unknown;
  /** Success copy for the DoneBlock (supports `**bold**` + `[[id|label]]`). */
  done: string;
  followup: FollowupChip[];
}

/* ── Thread history (persisted server-side) ─────────────────────────────── */

/** One row in the thread list (`GET /api/concierge/threads`). */
export interface ThreadSummary {
  id: string;
  title: string;
  preview: string;
  lastMessageAt: string;
}

/** One stored message in a thread (`GET /api/concierge/threads/:id`). A user
 *  message carries `text`; an assistant message carries rendered `blocks` (and
 *  may also carry `text` for a plain-prose reply). */
export interface StoredMessage {
  role: "user" | "assistant";
  text?: string;
  blocks?: ConvoBlock[];
}

export interface ThreadDetail {
  id: string;
  title: string;
  messages: StoredMessage[];
}

interface ListResponse<T> {
  data: T;
}

export const conciergeApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "conciergeApi",
    baseUrl: "/api/concierge",
  }),
  reducerPath: "conciergeApi",
  tagTypes: ["Threads", "Thread"],
  endpoints: (builder) => ({
    confirmAction: builder.mutation<ConfirmActionResponse, ConfirmActionRequest>(
      {
        query: (body) => ({
          url: "/confirm",
          method: "POST",
          body,
        }),
      },
    ),
    /** List the user's persisted concierge threads (most-recent first). */
    getThreads: builder.query<ListResponse<ThreadSummary[]>, void>({
      query: () => "/threads",
      providesTags: ["Threads"],
    }),
    /** Load a single thread with its full stored message list (for replay). */
    getThread: builder.query<ListResponse<ThreadDetail>, string>({
      query: (id) => `/threads/${id}`,
      providesTags: (_res, _err, id) => [{ type: "Thread", id }],
    }),
  }),
});

export const {
  useConfirmActionMutation,
  useGetThreadsQuery,
  useLazyGetThreadsQuery,
  useLazyGetThreadQuery,
} = conciergeApi;
